import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { useAssetContext } from '@/context/AssetContext';
import { 
  ArrowLeft,
  Search,
  Calendar,
  MapPin,
  GraduationCap,
  Trophy,
  DollarSign,
  CheckCircle2,
  ExternalLink,
  Clock,
  Loader2,
  BookmarkCheck,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';

const API_URL = window.location.origin;

const SearchDetailPage = () => {
  const { isDark } = useTheme();
  const { getLogo } = useAssetContext();
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, isAuthenticated } = useStudentAuth();
  
  const [loading, setLoading] = useState(true);
  const [searchData, setSearchData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/uac-desk/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchSearchData = async () => {
      if (!sessionId || !user?.user_id) return;
      
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/search/session/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          setSearchData(data);
        } else if (res.status === 404) {
          setError('Search not found or has expired');
        } else {
          setError('Failed to load search results');
        }
      } catch (err) {
        console.error('Error fetching search:', err);
        setError('Failed to load search results');
      } finally {
        setLoading(false);
      }
    };

    fetchSearchData();
  }, [sessionId, user]);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown date';
    }
  };

  if (authLoading || loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-ux-cyan" />
          <p className={isDark ? 'text-white/60' : 'text-gray-500'}>Loading search results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
        <header className={`border-b ${isDark ? 'bg-[#233842] border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/uac-desk/dashboard">
                <Button variant="ghost">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
              <Link to="/">
                <img src={getLogo()} alt="UniXperts" className={`h-8 w-auto ${isDark ? 'brightness-0 invert' : ''}`} />
              </Link>
            </div>
          </div>
        </header>
        
        <main className="max-w-2xl mx-auto px-4 py-16 text-center">
          <AlertCircle className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
          <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Search Not Found
          </h1>
          <p className={`mb-6 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{error}</p>
          <div className="flex gap-3 justify-center">
            <Link to="/uac-desk/dashboard">
              <Button variant="outline">Go to Dashboard</Button>
            </Link>
            <Link to="/uac-desk/ai-finder">
              <Button className="bg-gradient-to-r from-ux-cyan to-ux-blue">
                New Search
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const recommendations = searchData?.results?.recommendations || [];
  const searchProfile = searchData?.search_profile || {};

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${isDark ? 'bg-[#233842] border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/uac-desk/dashboard">
              <Button variant="ghost">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              {searchData?.saved && (
                <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
                  <BookmarkCheck className="w-3 h-3" />
                  Saved
                </Badge>
              )}
              <Link to="/">
                <img src={getLogo()} alt="UniXperts" className={`h-8 w-auto ${isDark ? 'brightness-0 invert' : ''}`} />
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Search Info Card */}
        <Card className={`mb-6 ${isDark ? 'bg-white/5 border-white/10' : ''}`}>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  {searchData?.session_name || 'Saved Search Results'}
                </h1>
                <div className={`flex flex-wrap items-center gap-4 mt-2 text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(searchData?.created_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Search className="w-4 h-4" />
                    {recommendations.length} universities found
                  </span>
                </div>
              </div>
              <Link to="/uac-desk/ai-finder">
                <Button className="bg-gradient-to-r from-ux-cyan to-ux-blue">
                  New Search
                </Button>
              </Link>
            </div>

            {/* Search Criteria Summary */}
            {(searchProfile.preferred_countries?.length > 0 || searchProfile.preferred_majors?.length > 0) && (
              <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <p className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  Search Criteria
                </p>
                <div className="flex flex-wrap gap-2">
                  {searchProfile.preferred_countries?.map((country, i) => (
                    <Badge key={i} variant="outline" className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {country}
                    </Badge>
                  ))}
                  {searchProfile.preferred_majors?.slice(0, 3).map((major, i) => (
                    <Badge key={i} variant="secondary" className="flex items-center gap-1">
                      <GraduationCap className="w-3 h-3" />
                      {major}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Grid */}
        {recommendations.length === 0 ? (
          <div className={`text-center py-12 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
            <Search className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
            <p className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-600'}`}>
              No results in this search
            </p>
            <p className={`text-sm mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              Try a new search with different criteria
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendations.map((uni, index) => (
              <Card 
                key={index}
                className={`hover:shadow-lg transition-all ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : ''}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold line-clamp-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        {uni.university_name}
                      </h3>
                      <p className={`text-sm flex items-center gap-1 mt-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        {uni.country}
                      </p>
                    </div>
                    {uni.match_score && (
                      <Badge className="bg-ux-violet/10 text-ux-violet ml-2">
                        {uni.match_score}% match
                      </Badge>
                    )}
                  </div>

                  <p className={`text-sm mb-3 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                    {uni.program || 'Various Programs'}
                  </p>

                  <div className={`flex items-center gap-3 text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                    {uni.ranking && (
                      <span className="flex items-center gap-1">
                        <Trophy className="w-4 h-4 text-amber-500" />
                        #{uni.ranking}
                      </span>
                    )}
                    {uni.tuition_usd && (
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4 text-emerald-500" />
                        ${uni.tuition_usd.toLocaleString()}/yr
                      </span>
                    )}
                  </div>

                  {uni.reasons?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-dashed border-gray-200 dark:border-white/10">
                      <p className={`text-xs mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Why this fits:</p>
                      <ul className="space-y-1">
                        {uni.reasons.slice(0, 2).map((reason, i) => (
                          <li key={i} className={`text-xs flex items-start gap-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                            <CheckCircle2 className="w-3 h-3 text-ux-green flex-shrink-0 mt-0.5" />
                            <span className="line-clamp-1">{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-4 pt-3 border-t border-gray-200 dark:border-white/10">
                    <Link 
                      to={`/universities/${uni.university_name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                    >
                      <Button size="sm" className="w-full bg-gradient-to-r from-ux-cyan to-ux-blue">
                        View Details
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default SearchDetailPage;
